import { useEffect } from "react";
import { useParams, Link, useMatch } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { useStateMachine } from "@/core/arch/state_machine";
import { MoneyRepository } from "@/application/money/money_repository";
import { TransactionDetailBloc } from "@/application/money/transaction_detail_bloc";
import { ROUTES } from "@/routes";
import { Button } from "@/components/ui/base/button";
import { MoneyStatusBadge } from "./components/MoneyStatusBadge";
import { CopyButton } from "./components/CopyButton";
import { TransactionSummaryCard } from "./components/TransactionSummaryCard";
import { FeesCard } from "./components/FeesCard";
import { TendersCard } from "./components/TendersCard";
import { DisputeCard } from "./components/DisputeCard";
import { RefundCard } from "./components/RefundCard";
import { ReceiptCard } from "./components/ReceiptCard";
import { InvoiceCard } from "./components/InvoiceCard";
import { RelatedEventsLink } from "./components/RelatedEventsLink";

// ── FailureCard ────────────────────────────────────────────────────────────────

interface FailureDetails {
  code?: string | null;
  message?: string | null;
  declineCode?: string | null;
}

function FailureCard({ details }: { details: FailureDetails }) {
  return (
    <div className="bg-card rounded-lg border border-l-4 border-l-red-400 p-5">
      <h2 className="mb-3 text-sm font-semibold text-red-700">Failure</h2>
      <div className="divide-y text-sm">
        {details.code && (
          <div className="flex justify-between py-2.5">
            <span className="text-muted-foreground">Code</span>
            <span className="text-foreground font-mono text-xs">
              {details.code}
            </span>
          </div>
        )}
        {details.message && (
          <div className="flex flex-col gap-1 py-2.5">
            <span className="text-muted-foreground">Message</span>
            <span className="text-foreground">{details.message}</span>
          </div>
        )}
        {details.declineCode && (
          <div className="flex justify-between py-2.5">
            <span className="text-muted-foreground">Decline code</span>
            <span className="text-foreground font-mono text-xs">
              {details.declineCode}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── View ──────────────────────────────────────────────────────────────────────

export function TransactionDetailView() {
  const { businessId, txId, id } = useParams<{
    businessId?: string;
    txId: string;
    id?: string;
  }>();

  const terminalMatch = useMatch(ROUTES.TERMINAL_ACCOUNT_TRANSACTION_DETAIL);
  const isTerminalMode = terminalMatch !== null;

  const [state, bloc] = useStateMachine(
    () => new TransactionDetailBloc(new MoneyRepository()),
  );

  useEffect(() => {
    if (txId) {
      void bloc.load(txId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txId]);

  const merchantMoneyUrl = businessId
    ? `${ROUTES.MERCHANT_DETAIL.replace(":businessId", businessId)}?money=transactions`
    : ROUTES.MERCHANTS;

  const merchantName =
    state.transaction?.businessId ?? businessId ?? "Merchant";

  // ── Loading ──────────────────────────────────────────────────────────────

  if (state.status === "loading") {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────

  const earlyBackUrl =
    isTerminalMode && id
      ? `${ROUTES.ACCOUNT_DETAIL.replace(":id", id)}?tab=transactions`
      : merchantMoneyUrl;
  const earlyBackLabel = isTerminalMode
    ? "Back to terminal account"
    : `${merchantName} / Money`;

  if (state.status === "error") {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <Link
            to={earlyBackUrl}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            {earlyBackLabel}
          </Link>
        </div>
        <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-sm text-red-700">
            Failed to load transaction: {state.error}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => txId && void bloc.retry(txId)}
            className="gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────

  if (state.status === "not-found") {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <Link
            to={earlyBackUrl}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            {earlyBackLabel}
          </Link>
        </div>
        <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
          <p className="text-base font-medium">Transaction not found</p>
          <p className="mt-1 text-sm">
            The transaction <span className="font-mono text-xs">{txId}</span>{" "}
            does not exist.
          </p>
          <Link
            to={earlyBackUrl}
            className="mt-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="h-4 w-4" />
            {isTerminalMode ? "Back to terminal account" : "Back to merchant"}
          </Link>
        </div>
      </div>
    );
  }

  if (!state.transaction) return null;

  const tx = state.transaction;

  const resolvedBusinessId = tx.businessId ?? businessId ?? "";

  const backUrl =
    isTerminalMode && id
      ? `${ROUTES.ACCOUNT_DETAIL.replace(":id", id)}?tab=transactions`
      : resolvedBusinessId
        ? `${ROUTES.MERCHANT_DETAIL.replace(":businessId", resolvedBusinessId)}?money=transactions`
        : ROUTES.MERCHANTS;

  const backLabel = isTerminalMode
    ? "Back to terminal account"
    : `${resolvedBusinessId} / Money`;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">
      {/* Back nav */}
      <div>
        <Link
          to={backUrl}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>
      </div>

      {/* Header card */}
      <div className="bg-card rounded-lg border p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {tx.status && <MoneyStatusBadge status={tx.status} />}
            {tx.chargeType && (
              <span className="bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 text-xs font-medium">
                {tx.chargeType}
              </span>
            )}
          </div>
          {tx.cposPayId && (
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground font-mono text-sm">
                {tx.cposPayId}
              </span>
              <CopyButton value={tx.cposPayId} />
            </div>
          )}
        </div>
      </div>

      {/* Summary + Amount breakdown — 2-column grid */}
      <div className="grid gap-6 md:grid-cols-2">
        <TransactionSummaryCard transaction={tx} />
        <FeesCard transaction={tx} />
      </div>

      {/* Terminal tender mix — renders only for a split sale */}
      <TendersCard transaction={tx} />

      {/* Conditional cards in priority order */}
      <div className="flex flex-col gap-6">
        {/* 1. Dispute */}
        {tx.dispute != null && <DisputeCard transaction={tx} />}

        {/* 2. Refund */}
        {tx.refundedAmount != null && tx.refundedAmount > 0 && (
          <RefundCard transaction={tx} />
        )}

        {/* 3. Failure */}
        {tx.failureDetails != null && (
          <FailureCard details={tx.failureDetails as FailureDetails} />
        )}

        {/* 4. Receipts — always render */}
        <ReceiptCard receipts={state.receipts} />

        {/* 5. Invoice — when invoiceId set and invoice fetched */}
        {tx.invoiceId != null && state.invoice != null && (
          <InvoiceCard invoice={state.invoice} transaction={tx} />
        )}

        {/* 6. Related events — always render */}
        <RelatedEventsLink transaction={tx} />
      </div>
    </div>
  );
}
